import { Progress, Tooltip } from 'antd';
import moment, { Moment } from 'moment';
import React, { FC } from 'react';
import styled from 'styled-components';
import { DateType } from 'api/models/core-types';
import { PhaseStatusProgressDates } from 'components/atoms/progress';
import { TaskCounterProp } from 'data/projects';
import { useTranslation } from 'hooks/use-translation';
import { CurrentDateIndicator } from './current-date-indicator';

const Wrapper = styled.div`
  height: 22px;
  line-height: 22px;
  margin-bottom: var(--spacing-03);
  position: relative;
`;
// Move to atoms if required.
const ProgressBar = styled(Progress)`
  --progress-remaining-color: var(--coolest-gray-30);

  clip-path: polygon(98% 0, 100% 50%, 98% 98%, 0% 100%, 2% 50%, 0% 0%);
  line-height: 22px;
  margin-left: -4px;
  width: calc(100% + 10px);

  .ant-progress-inner,
  .ant-progress-success-bg,
  .ant-progress-bg {
    border-radius: 0;
  }

  .ant-progress-success-bg {
    background-color: var(--green-40);
  }

  .ant-progress-bg {
    background-color: var(--green-30);
  }
`;
const StyledPara = styled.p`
  width: max-content;
`;

export const calculateCurrentDateIndicatorPercentage = (
  momentCurrentDate: Moment,
  momentStartDate: Moment,
  momentDueDate: Moment,
): number => {
  const totalDistance = momentDueDate.diff(momentStartDate, 'day');
  const distanceLeft = momentDueDate.diff(momentCurrentDate, 'day');

  return Math.floor(((totalDistance - distanceLeft) / totalDistance) * 100);
};

export const PhaseStatusProgress: FC<{
  className?: string;
  startDate: DateType;
  dueDate: DateType;
  total: number;
  complete: number;
  progress: number;
  subTasks: TaskCounterProp;
  firstLevelTaskCounter: TaskCounterProp;
}> = ({
  className,
  startDate,
  dueDate,
  total,
  complete,
  progress,
  subTasks,
  firstLevelTaskCounter,
}) => {
  const completeStatus = (complete / total) * 100;
  const totalStatus = ((complete + progress) / total) * 100;
  const momentCurrentDate = moment().startOf('day');
  const momentStartDate = moment(startDate).startOf('day');
  const momentDueDate = moment(dueDate).startOf('day');
  const currentDateIndicatorPercentage =
    calculateCurrentDateIndicatorPercentage(
      momentCurrentDate,
      momentStartDate,
      momentDueDate,
    );
  const hasAnyInProgress =
    firstLevelTaskCounter.inProgress + subTasks.inProgress > 0;
  const hasAnyCompleted =
    firstLevelTaskCounter.completed + subTasks.completed > 0;
  const hasAnyTodo = firstLevelTaskCounter.todo + subTasks.todo > 0;
  const hasAnyBlocked = firstLevelTaskCounter.blocked + subTasks.blocked > 0;

  const completeText = useTranslation(
    'projects.plan.phase.progress.completed',
    {
      tasks: firstLevelTaskCounter.completed,
      subTasks: subTasks.completed,
      hasTasksAndSubtasks:
        firstLevelTaskCounter.completed > 0 && subTasks.completed > 0,
      b: (chunks) => <b>{chunks}</b>,
    },
  );
  const inProgressText = useTranslation(
    'projects.plan.phase.progress.in_progress',
    {
      tasks: firstLevelTaskCounter.inProgress,
      subTasks: subTasks.inProgress,
      hasTasksAndSubtasks:
        firstLevelTaskCounter.inProgress > 0 && subTasks.inProgress > 0,
      b: (chunks) => <b>{chunks}</b>,
    },
  );
  const todoText = useTranslation('projects.plan.phase.progress.todo', {
    tasks: firstLevelTaskCounter.todo,
    subTasks: subTasks.todo,
    hasTasksAndSubtasks: firstLevelTaskCounter.todo > 0 && subTasks.todo > 0,
    b: (chunks) => <b>{chunks}</b>,
  });
  const blockedText = useTranslation('projects.plan.phase.progress.blocked', {
    tasks: firstLevelTaskCounter.blocked,
    subTasks: subTasks.blocked,
    hasTasksAndSubtasks:
      firstLevelTaskCounter.blocked > 0 && subTasks.blocked > 0,
    b: (chunks) => <b>{chunks}</b>,
  });

  const canShowCurrentDateIndicator =
    currentDateIndicatorPercentage >= 0 &&
    currentDateIndicatorPercentage <= 100;

  return (
    <Wrapper className={className}>
      <Tooltip
        title={
          <>
            {hasAnyInProgress ? (
              <StyledPara>{inProgressText}</StyledPara>
            ) : null}
            {hasAnyCompleted ? <StyledPara>{completeText}</StyledPara> : null}
            {hasAnyBlocked ? <StyledPara>{blockedText}</StyledPara> : null}
            {hasAnyTodo ? <StyledPara>{todoText}</StyledPara> : null}
          </>
        }
        overlayInnerStyle={{
          width: 'fit-content',
        }}
      >
        <ProgressBar
          percent={totalStatus}
          success={{ percent: completeStatus }}
          showInfo={false}
          status="success"
          strokeWidth={22}
        />
      </Tooltip>
      {canShowCurrentDateIndicator && (
        <CurrentDateIndicator
          currentDate={momentCurrentDate}
          result={currentDateIndicatorPercentage}
        />
      )}
      <PhaseStatusProgressDates
        dueDate={dueDate}
        startDate={startDate}
        className="progress-title"
      />
    </Wrapper>
  );
};
